/**
 * Tabellen als Datei ausgeben — für Excel, für den Steuerberater, für den
 * Anhang an eine Rechnung.
 *
 * ═══ WARUM SEMIKOLON UND KEIN KOMMA ═══
 *
 * Weil die Datei in einem deutschen Excel geöffnet wird. Dort ist das Komma
 * das Dezimaltrennzeichen, und eine mit Komma getrennte Datei landet komplett
 * in Spalte A. Das ist kein Schönheitsfehler: Wer eine kaputte Datei bekommt,
 * tippt die Zahlen ab — und dann ist der ganze Zweck der Ausgabe weg.
 *
 * ═══ WARUM DIESE DREI BYTES AM ANFANG ═══
 *
 * Das BOM (`\uFEFF`) sagt Excel, dass die Datei UTF-8 ist. Ohne es wird aus
 * „Schemmerhofen" ein „Schemmerhofen" und aus jedem ö ein Kästchen. Excel
 * erkennt UTF-8 nicht von selbst, wenn keine Kennung dasteht.
 *
 * ═══ WARUM CSV UND KEIN ECHTES XLSX ═══
 *
 * Ein echtes Excel-Format bräuchte eine Bibliothek von rund einem halben
 * Megabyte im Browser — für eine Datei, die in Excel, LibreOffice, Numbers und
 * jedem Buchhaltungsprogramm ohnehin identisch aussieht. CSV lässt sich
 * außerdem in zehn Jahren noch öffnen; ein XLSX aus 2026 vielleicht nicht.
 *
 * Excel öffnet die Datei per Doppelklick richtig, weil Semikolon und BOM
 * stimmen. Die Endung heißt trotzdem `.csv` — eine Datei `.xlsx` zu nennen,
 * die keine ist, wäre die Art von Trick, die genau einmal funktioniert.
 *
 * ═══ WARUM MANCHE TEXTE MIT EINEM APOSTROPH ANFANGEN (H1.7) ═══
 *
 * Beginnt ein Feld mit `=`, `+`, `-` oder `@`, rechnet Excel es als Formel —
 * auch in Anführungszeichen. Aus einem eingetippten Ladeort „-Schotter" wird
 * `#NAME?`, aus „+49 7351 …" eine Rechnung, und ein „=HYPERLINK(…)" wird zum
 * anklickbaren Link in einer Datei, die von Haid & Jerg an den Kunden geht
 * („CSV-Injection", OWASP). Tabulator und Wagenrücklauf vorne zählen mit,
 * weil Excel sie beim Einlesen entfernen kann und dahinter ein Formelzeichen
 * steht.
 *
 * Solche TEXTE bekommen deshalb ein `'` davor; Excel liest sie dann als Text.
 * Zahlen nicht: Die erzeugt das Programm selbst, sie können keine Formel sein,
 * und eine Gutschrift muss in Excel eine negative Zahl bleiben.
 *
 * Verworfen: Formelzeichen entfernen (verfälscht den Inhalt — aus
 * „+49 …" würde „49 …") und Leerzeichen davor (erkennt nicht jedes
 * Programm als Schutz). Der Preis: In seltenen Fällen steht das `'` in
 * Programmen außer Excel sichtbar da. Ein Zeichen zu viel ist besser als
 * eine Formel in der Datei des Kunden.
 */

/** Eine Spalte der Ausgabe. Reihenfolge = Reihenfolge in der Datei. */
export type Ausgabespalte<T> = {
  kopf: string;
  wert: (zeile: T) => string | number | null;
  /**
   * Zahl mit Komma statt Punkt ausgeben. Nur für echte Beträge und Mengen —
   * eine Auftragsnummer ist keine Zahl, auch wenn sie aus Ziffern besteht.
   */
  zahl?: boolean;
};

/**
 * Ein Feld für die Datei aufbereiten.
 *
 * Anführungszeichen kommen immer, nicht nur wenn nötig. Ein Ortsname mit
 * Semikolon („Werk Nord; Tor 3") zerlegt sonst die Zeile, und dieser Fall
 * fällt erst auf, wenn er das erste Mal auftritt — beim Kunden, im Anhang
 * einer Rechnung.
 */
function feld(wert: string | number | null, zahl: boolean | undefined): string {
  if (wert === null || wert === undefined) return '""';

  if (zahl && typeof wert === 'number') {
    return `"${wert.toFixed(2).replace('.', ',')}"`;
  }

  // Nur Texte schützen: Eine Zahl wie -3 (Minuten, Anzahl) kann keine Formel
  // sein und soll in Excel eine Zahl bleiben.
  const text = typeof wert === 'string' ? formelSicher(wert) : String(wert);
  return `"${text.replace(/"/g, '""')}"`;
}

/**
 * Wann ein Text als Formel gelesen werden könnte: Tab oder CR ganz vorne (laut
 * OWASP), oder ein Formelzeichen — auch hinter Leerraum, weil manche Importe
 * Leerzeichen am Rand abschneiden, und auch in Vollbreite (＝＋－＠), weil
 * manche Programme diese in die gewöhnlichen umwandeln. `\s` umfasst in
 * JavaScript auch das geschützte Leerzeichen und das BOM.
 */
const FORMELANFANG = /^(?:[\t\r]|\s*[=+\-@＝＋－＠])/;

/**
 * Einen Text so aufbereiten, dass Excel ihn nicht als Formel liest — siehe
 * Kopfkommentar „WARUM MANCHE TEXTE MIT EINEM APOSTROPH ANFANGEN".
 */
function formelSicher(text: string): string {
  return FORMELANFANG.test(text) ? `'${text}` : text;
}

export function alsCsv<T>(zeilen: T[], spalten: Ausgabespalte<T>[]): string {
  const kopf = spalten.map((s) => feld(s.kopf, false)).join(';');
  const leib = zeilen.map((zeile) =>
    spalten.map((s) => feld(s.wert(zeile), s.zahl)).join(';'),
  );
  // Zeilenende mit CR LF: Das erwartet Excel unter Windows, und alles andere
  // kommt damit auch zurecht.
  return '\uFEFF' + [kopf, ...leib].join('\r\n') + '\r\n';
}

/**
 * Die Datei zum Herunterladen anbieten.
 *
 * Ohne Server, ohne Zwischenspeicher: Der Text wird im Browser zu einer Datei
 * und sofort wieder verworfen. Das ist nicht nur einfacher, es ist auch der
 * einzige Weg, bei dem Kundenzahlen nirgendwo unterwegs liegen bleiben.
 */
export function herunterladen(dateiname: string, inhalt: string): void {
  const brocken = new Blob([inhalt], { type: 'text/csv;charset=utf-8' });
  const adresse = URL.createObjectURL(brocken);

  const verweis = document.createElement('a');
  verweis.href = adresse;
  verweis.download = dateiname;
  document.body.appendChild(verweis);
  verweis.click();
  document.body.removeChild(verweis);

  // Ohne dieses Aufräumen bleibt die Datei im Speicher des Browsers liegen,
  // bis der Tab geschlossen wird. Bei zwanzig Ausgaben am Tag summiert sich das.
  URL.revokeObjectURL(adresse);
}

/**
 * Ein Dateiname, der im Downloadordner noch etwas sagt.
 *
 * „export.csv" ist nach drei Ausgaben „export (3).csv" und niemand weiß mehr,
 * welcher Kunde das war. Deshalb: Zweck, Gegenstand, Datum — und alles, was
 * ein Dateisystem stört, raus.
 */
export function dateiname(zweck: string, gegenstand: string, stand: Date): string {
  const sauber = gegenstand
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);

  const tag = `${stand.getFullYear()}-${String(stand.getMonth() + 1).padStart(2, '0')}-${String(stand.getDate()).padStart(2, '0')}`;

  return [zweck, sauber, tag].filter(Boolean).join('_') + '.csv';
}
